import React, { useState } from 'react';
import { useAgentPrompts } from './hooks/useAgentPrompts';
import { AgentPromptEditor } from './components/AgentPromptEditor';
import { VersionHistory } from './components/VersionHistory';
import { VersionDiff } from './components/VersionDiff';
import { Card, Alert, Spinner, Button } from '@/components/ui';
import type { AgentPromptVersion } from '@/services/admin/AdminService';

// Document Icon for Agent Instructions
const DocumentTextIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className || 'w-6 h-6'}
  >
    <path
      fillRule="evenodd"
      d="M5.625 1.5c-1.036 0-1.875.84-1.875 1.875v17.25c0 1.035.84 1.875 1.875 1.875h12.75c1.035 0 1.875-.84 1.875-1.875V12.75A3.75 3.75 0 0016.5 9h-1.875a1.875 1.875 0 01-1.875-1.875V5.25A3.75 3.75 0 009 1.5H5.625zM7.5 15a.75.75 0 01.75-.75h7.5a.75.75 0 010 1.5h-7.5A.75.75 0 017.5 15zm.75 2.25a.75.75 0 000 1.5H12a.75.75 0 000-1.5H8.25z"
      clipRule="evenodd"
    />
    <path d="M12.971 1.816A5.23 5.23 0 0114.25 5.25v1.875c0 .207.168.375.375.375H16.5a5.23 5.23 0 013.434 1.279 9.768 9.768 0 00-6.963-6.963z" />
  </svg>
);

// Agent tabs configuration
const AGENT_TABS = [
  { key: 'discovery_expert', label: 'Discovery' },
  { key: 'design_expert', label: 'Design' },
  { key: 'systems_architect', label: 'Systems' },
  { key: 'technical_architect', label: 'Technical' },
  { key: 'code_expert', label: 'Code' },
  { key: 'operations_expert', label: 'Operations' },
];

export const AgentInstructionsSection: React.FC = () => {
  const {
    prompts,
    loading,
    error,
    actionLoading,
    addVersion,
    updatePrompt,
    rollbackVersion,
    syncFromCode,
  } = useAgentPrompts();

  const [activeTab, setActiveTab] = useState(AGENT_TABS[0].key);
  const [diffModalOpen, setDiffModalOpen] = useState(false);
  const [selectedVersion, setSelectedVersion] =
    useState<AgentPromptVersion | null>(null);

  const activePrompt = prompts.find((p) => p.agent_name === activeTab);

  const handleViewDiff = (version: AgentPromptVersion) => {
    setSelectedVersion(version);
    setDiffModalOpen(true);
  };

  const handleRollback = async (version: string) => {
    if (activePrompt) {
      await rollbackVersion(activePrompt.agent_name, version);
    }
  };

  const handleSyncFromCode = async () => {
    // First preview, then if changes exist, apply them
    const preview = await syncFromCode(true);
    if (preview && preview.agents_with_changes > 0) {
      const confirmed = window.confirm(
        `${preview.agents_with_changes} agent(s) will be updated from code. Apply changes?`
      );
      if (confirmed) {
        await syncFromCode(false);
      }
    }
  };

  return (
    <>
      <Card padding="lg">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <DocumentTextIcon className="w-6 h-6 mr-3 text-primary-600" />
            <h2 className="text-xl font-semibold text-secondary-900">
              Agent Instructions
            </h2>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleSyncFromCode}
            disabled={actionLoading}
          >
            {actionLoading ? 'Syncing...' : 'Sync from Code'}
          </Button>
        </div>

        <p className="text-secondary-600 mb-6">
          Manage and version control the instruction prompts for each workflow
          agent. Changes take effect immediately with rollback capability.
        </p>

        {/* Agent Tabs */}
        <div className="border-b border-secondary-200 mb-6">
          <nav className="flex gap-1 -mb-px overflow-x-auto">
            {AGENT_TABS.map((tab) => {
              const isActive = activeTab === tab.key;
              const prompt = prompts.find((p) => p.agent_name === tab.key);
              const hasPrompt = !!prompt;

              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`
                    px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors
                    ${
                      isActive
                        ? 'border-primary-600 text-primary-600'
                        : 'border-transparent text-secondary-500 hover:text-secondary-700 hover:border-secondary-300'
                    }
                    ${!hasPrompt ? 'opacity-50' : ''}
                  `}
                >
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : error ? (
          <Alert variant="error">{error}</Alert>
        ) : !activePrompt ? (
          <Alert variant="info">
            No prompt configuration found for this agent. The agent may use
            default instructions.
          </Alert>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <AgentPromptEditor
                prompt={activePrompt}
                onAddVersion={addVersion}
                onUpdatePrompt={updatePrompt}
                loading={actionLoading}
              />
            </div>
            <div className="lg:col-span-1">
              <VersionHistory
                versions={activePrompt.versions}
                currentVersion={activePrompt.current_version}
                onRollback={handleRollback}
                onViewDiff={handleViewDiff}
                loading={actionLoading}
              />
            </div>
          </div>
        )}
      </Card>

      <VersionDiff
        open={diffModalOpen}
        onClose={() => setDiffModalOpen(false)}
        version={selectedVersion}
        currentVersion={activePrompt?.current_version || ''}
      />
    </>
  );
};

export default AgentInstructionsSection;
